import { myinfoQueries } from "@app/config/queryFactory";
import { ERROR_CODES, HTTP_STATUS } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import { Button } from "@base/ui/Button/Button";
import { Dialog } from "@base/ui/Dialog";
import { deleteMeetingReservation } from "@features/reservation/api/meeting";
import { reservationQueries } from "@features/reservation/api/queryFactory";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  reservationId: number;
};

const DeleteReservationDialog = ({ isOpen, onClose, reservationId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (reservationId: number) => deleteMeetingReservation(reservationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() });
      queryClient.invalidateQueries({ queryKey: myinfoQueries.all() });
      toast.success(t("reservationDeletedSuccess"));
    },
    onError: (error) => {
      if (isAxiosApiError(error)) {
        const { code, status } = error.response.data;
        if (code === ERROR_CODES.PAST_RESERVATION && status === HTTP_STATUS.BAD_REQUEST) {
          toast.error(t("pastReservation"));
        } else {
          toast.error(t("reservationDeleteFailed"));
        }
      }
    },
    onSettled: () => {
      onClose();
    },
  });

  const handleDelete = () => {
    mutate(reservationId);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Content>
        <div className="flex flex-col gap-4 p-4 pt-6">
          <Dialog.Title>{t("deleteReservationTitle")}</Dialog.Title>
          <Dialog.Description>{t("deleteReservationDescription")}</Dialog.Description>
          <div className="flex gap-1 mt-2">
            <Button type="button" variant="ghost" color="secondary" className="w-full" onClick={onClose}>
              {t("keepReservation")}
            </Button>
            <Button type="button" variant="fill" color="danger" className="w-full" onClick={handleDelete}>
              {t("cancelReservation")}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteReservationDialog;
